import * as React from 'react';
import { useIntl } from 'react-intl';
import { sortPartialDate } from 'neuro-utils';
import HistorySection from 'Components/HistorySection';
import HistoryRow from 'Components/HistoryRow';
import HistoryRowControls from 'Components/HistoryRowControls';
import HistoryRowSubHeader from 'Components/HistoryRowSubHeader';
import HistoryRowVerticalItem from 'Components/HistoryRowVerticalItem';
import { EventListing, EventTable, StyledViewSelector } from './components';
import { TDCSContext } from '..';
import { Container, Item } from 'Components/Grid';
import { headerText } from 'Utility/ninmtUtil';
import { formatPartialDate } from 'Utility/date';

const HistoryRowData = ({
  d,
  fm,
  allDocs,
}: {
  d: IDocumentWithTypeV2<'tdcs'>;
  fm: (id: string) => string;
  allDocs: IDocumentWithTypeV2<'tdcs'>[];
}): React.JSX.Element => {
  const [selectedView, setSelectedView] = React.useState<'list' | 'table'>('list');
  const viewsArray: Array<'list' | 'table'> = ['list', 'table'];

  const allSessions = allDocs
    .flatMap(ad => ad.sessions)
    .filter(s => s !== undefined)
    .sort((s1, s2) => sortPartialDate(s2?.date, s1?.date)) as ITDCSSession[];

  return (
    <React.Fragment>
      {((Array.isArray(d.sessions) && d.sessions.length > 0) ||
        (Array.isArray(d.unusedSessions) && d.unusedSessions.length > 0) ||
        (Array.isArray(d.otherEvents) && d.otherEvents.length > 0)) && (
        <React.Fragment>
          <HistoryRowSubHeader
            header={
              <Container>
                <Item xs={3}>{fm('tdcs.sessionsAndProtocols')}</Item>
                {Array.isArray(d.sessions) && d.sessions.length > 0 && (
                  <Item xs={9}>
                    <Container justifyContent="flex-end">
                      <Item>
                        {viewsArray.map(v => (
                          <StyledViewSelector active={v === selectedView} key={v} onClick={() => setSelectedView(v)}>
                            {fm('general.' + v)}
                          </StyledViewSelector>
                        ))}
                      </Item>
                    </Container>
                  </Item>
                )}
              </Container>
            }
          />
          {
            {
              list: (
                <EventListing
                  type="sessions"
                  events={d.sessions ?? []}
                  secondaryEvents={[...(d.unusedSessions ?? []), ...(d.otherEvents ?? [])]}
                  eventsBeyondThisDoc={allSessions}
                  documentDate={d.date}
                />
              ),
              table: (
                <EventTable
                  type="sessions"
                  events={d.sessions ?? []}
                  secondaryEvents={[...(d.unusedSessions ?? []), ...(d.otherEvents ?? [])]}
                  eventsBeyondThisDoc={allSessions}
                  documentDate={d.date}
                />
              ),
            }[selectedView]
          }
        </React.Fragment>
      )}
      {Array.isArray(d.complications) && d.complications.length > 0 && (
        <React.Fragment>
          <HistoryRowSubHeader header={fm('tdcs.complications')} />
          <EventListing type="complications" events={d.complications ?? []} />
        </React.Fragment>
      )}
      {Array.isArray(d.adverseEffects) && d.adverseEffects.length > 0 && (
        <React.Fragment>
          <HistoryRowSubHeader header={fm('tdcs.adverseEffects')} />
          <EventListing type="adverseEffects" events={d.adverseEffects ?? []} />
        </React.Fragment>
      )}
      {d.hasEnded && (
        <React.Fragment>
          <HistoryRowSubHeader header={fm('tdcs.ending')} />
          <HistoryRowVerticalItem header={fm('tdcs.endDate')} value={d.endDate ? formatPartialDate(d.endDate) : '-'} />
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

const History = (): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  return (
    <TDCSContext.Consumer>
      {({ documents, startEdit }) => (
        <React.Fragment>
          {documents.filter(d => !d.hasEnded).length > 0 && (
            <HistorySection headerText={fm('tdcs.tdcsOngoing')} hasHistoryRows={true}>
              {documents
                .filter(d => !d.hasEnded)
                .map(d => (
                  <HistoryRow
                    headerText={headerText(d, fm)}
                    key={d._id}
                    rowButton={<HistoryRowControls document={d} startEdit={startEdit} />}
                    controlsMargin={false}
                  >
                    <HistoryRowData d={d} fm={fm} allDocs={documents} />
                  </HistoryRow>
                ))}
            </HistorySection>
          )}
          {documents.filter(d => d.hasEnded).length > 0 && (
            <HistorySection headerText={fm('tdcs.tdcsEnded')} inactive={true} hasHistoryRows={true}>
              {documents
                .filter(d => d.hasEnded)
                .map(d => (
                  <HistoryRow
                    headerText={headerText(d, fm)}
                    key={d._id}
                    rowButton={<HistoryRowControls document={d} startEdit={startEdit} />}
                    controlsMargin={false}
                  >
                    <HistoryRowData d={d} fm={fm} allDocs={documents} />
                  </HistoryRow>
                ))}
            </HistorySection>
          )}
        </React.Fragment>
      )}
    </TDCSContext.Consumer>
  );
};

export default History;
